import { UserStorageService } from './../user-storage/user-storage.service';
import { Injectable, signal } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import {
  AttributeBase,
  Leg,
  ProductSpecification,
  StrategyDetailTabsStateKey
} from '@approot/models';
import { AggregatedValues, LegsToAggregate } from '@approot/models/summed-legs';
import { ScheduleUtilities, clone, medusaEnviroment } from '@approot/utilities';
import { LegComponent } from '@approot/widgets';
import { getAttributeControlFromKey } from '@approot/widgets/strategy-detail/leg/utilities/leg-attribute-utilities';
import {
  MARKET_DATA_LIVE,
  MARKET_DATA_PAST_DATE,
  marketDataDateType
} from '../pricing-extra/pricing-extra.service';
import { LegService } from '../leg/leg.service';
import { isEqual } from 'lodash';

export interface Tabs {
  selectedTab: number;
  tabStatus: 'loading' | 'ready' | 'shouldRestore';
  tabs: Tab[];
}

export interface Tab {
  name: string;
  legs: Leg[];
  legsToAggregate: LegsToAggregate;
  aggregateLegsEntity?: string;
  tradeViewMode: boolean;
  liveMode: boolean;
  aggregatedValues?: AggregatedValues;
  marketData: {
    lastCurrentDateUsed: marketDataDateType;
    marketDataToUse: marketDataDateType;
    frozenTime: string;
  };
  firstLegTradeId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class StrategyDetailService {
  public currentLegComponents: LegComponent[] = [];
  public readonly defaultTab: Tab = {
    name: 'Unnamed Tab',
    legs: [],
    legsToAggregate: null,
    liveMode: false,
    tradeViewMode: false,
    marketData: {
      lastCurrentDateUsed: MARKET_DATA_LIVE,
      marketDataToUse: MARKET_DATA_LIVE,
      frozenTime: ''
    }
  };
  public tabsState = signal<Tabs>({
    selectedTab: 0,
    tabStatus: null,
    tabs: [clone(this.defaultTab)]
  });
  public savingTabInterval: number;
  public loadedTabs: Tabs;

  constructor(
    private legService: LegService,
    private userStorageService: UserStorageService
  ) {
    const minute = 60_000;
    const timeToSave = medusaEnviroment() !== 'uat' ? minute : minute * 5;
    this.savingTabInterval = setInterval(_ => this.saveTabs(), timeToSave);
  }

  public onTabSwitch(index: number, updateLegs = true): void {
    if (updateLegs) {
      this.saveAllLegsInTab();
    }

    this.tabsState.update(state => {
      const clonedState = clone(state);
      clonedState.selectedTab = index;
      return clonedState;
    });
  }

  public saveAllLegsInTab(): void {
    this.tabsState.update(state => {
      const clonedState = clone(state);
      const currentTab = clonedState.tabs[clonedState.selectedTab];
      currentTab.legs = this.currentLegComponents.map(x =>
        this.getLegForTab(x)
      );

      this.currentLegComponents.forEach(
        (legComponent: LegComponent, index: number) => {
          const allAttributeNames = Object.keys(legComponent.form.controls);
          currentTab.legs[index].legAttributeDataFromTab =
            this.getTabLegAttributes(
              allAttributeNames,
              legComponent.leg.attributesAsFormControls
            );
        }
      );

      if (currentTab.marketData.marketDataToUse !== MARKET_DATA_PAST_DATE) {
        currentTab.marketData.frozenTime = '';
      }
      return clonedState;
    });
  }

  public getLegForTab(legComponent: LegComponent): Leg {
    const legClone = clone(legComponent.leg);
    legClone.controlsStatus = {};
    legClone.preSetPosition = legComponent.form.getRawValue();
    legClone.fromTab = true; // leg will be seen as from the stored tab
    legClone.disablePricingButton = false;
    return legClone;
  }

  public get tradingMode(): boolean {
    return this.currentTab.tradeViewMode;
  }

  public setTabTradingMode(mode: boolean): void {
    this.tabsState.update(state => {
      const clonedState = clone(state);
      if (mode) {
        clonedState.tabs[clonedState.selectedTab].firstLegTradeId =
          new Date().getTime().toString() + 1;
      }
      clonedState.tabs[clonedState.selectedTab].tradeViewMode = mode;
      return clonedState;
    });
  }

  public get currentTab(): Tab {
    const tabsInfo = this.tabsState();
    return tabsInfo.tabs[tabsInfo.selectedTab];
  }

  public get marketDataToUse(): marketDataDateType {
    return this.currentTab.marketData.marketDataToUse;
  }

  public saveTabs(): void {
    if (this.tabsState().tabStatus !== 'ready') {
      return;
    }
    this.saveAllLegsInTab();
    const storedState = clone(this.tabsState());
    storedState.tabs.forEach(tab => {
      tab.legs.forEach(leg => {
        delete leg.attributesAsFormControls;
        delete leg.attributes;
        delete leg.tradeAttributes;
      });
    });
    storedState.tabs.forEach(x => delete x.firstLegTradeId);
    this.userStorageService.setItem(StrategyDetailTabsStateKey, storedState);
  }

  public localStorageTabs(): Tabs {
    return this.userStorageService.getItem<Tabs>(StrategyDetailTabsStateKey);
  }

  public addLegToStructuringScreen(
    productName: string,
    newProduct: boolean,
    preSetPosition: any,
    tradeViewMode: boolean
  ): void {
    if (this.tabsState().tabStatus !== 'ready') {
      this.setTabsAsReady();
    }
    this.legService.addLeg({
      productName,
      newProduct,
      preSetPosition,
      tradeViewMode
    });
  }

  public localStorageStrategyTabs(): void {
    this.loadedTabs = this.localStorageTabs();
    const loadedClone = clone(this.loadedTabs);
    const currentTabsClone = clone(this.tabsState());
    delete loadedClone?.tabStatus;
    delete currentTabsClone?.tabStatus;

    if (loadedClone && !isEqual(loadedClone, currentTabsClone)) {
      this.tabsState.update(state => {
        const clonedState = clone(state);
        clonedState.tabStatus = 'shouldRestore';
        return clonedState;
      });
    }
  }

  public restoreLoadedTabs(): void {
    this.loadedTabs.tabs.forEach(x => {
      x.legs = x.legs
        .map(leg => {
          this.forceLegToLatestVersion(leg); // can be removed in the future
          return this.legService.updateLegAttributes(leg);
        })
        .filter(leg => leg);
    });
    this.tabsState.set(this.loadedTabs);
    this.setTabsAsReady();
  }

  public setTabsAsReady(): void {
    this.tabsState.update(state => {
      const clonedState = clone(state);
      clonedState.tabStatus = 'ready';
      return clonedState;
    });
  }

  private forceLegToLatestVersion(leg: Leg): void {
    // Force legs to use new version of tab legs when loaded
    // can be removed in the future only needed for back compat
    if (!leg.legAttributeDataFromTab) {
      leg.legAttributeDataFromTab = this.getTabLegAttributes(
        Object.keys(leg.preSetPosition),
        leg.attributesAsFormControls
      );
      delete leg.attributesAsFormControls;
      delete leg.attributes;
      delete leg.tradeAttributes;
    }
  }

  private getTabLegAttributes(
    allAttributeNames: string[],
    attributesAsFormControls: AttributeBase<any>[]
  ): Partial<AttributeBase<any>>[] {
    return allAttributeNames
      .map(attr => {
        const ctrl = getAttributeControlFromKey(attr, attributesAsFormControls);
        if (!ctrl) {
          return null;
        }
        return {
          key: ctrl.key,
          textColor: ctrl.textColor,
          title: ctrl.title,
          info: ctrl.info
        };
      })
      .filter(attribute => attribute);
  }
}
